/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { IPublicClientApplication } from "@azure/msal-browser";
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { CircularProgress, StyledEngineProvider } from "@mui/material";
import { Cache } from "aws-amplify";
import React, { useCallback, useEffect, useState } from "react";
import MediaQuery from "react-responsive";
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';

import "./styles/App.css";
import "./styles/Navbar.css";

import { version } from '../package.json';
import AccountProfile from "./accountProfile";
import Announcements from "./announcements";
import { msalConfig } from "./authConfig";
import CacheBuster from './cacheBuster';
import Breadcrumbs from './Component/Breadcrumbs';
import Footer from "./Component/footer";
import Header from "./Component/header";
import Invoice from "./Component/invoice";
import Order from "./Component/order";
import Registration from "./Component/registration";
import ScrollToTop from "./Component/scrollToTop";
import Dashboard from "./dashboard";
import Navbar from "./navBar";
import UserService from "./services/UserService";


/**
 * Most applications will need to conditionally render certain components based on whether a user is signed in or not. 
 * msal-react provides 2 easy ways to do this. AuthenticatedTemplate and UnauthenticatedTemplate components will 
 * only render their children if a user is authenticated or unauthenticated, respectively. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const MainContent = () => {
    const { accounts, instance } = useMsal();
    const [userLoaded, setUserLoaded] = useState<boolean>(false);
    const [uName, setuName] = useState<string | null>("");
    const [uRole, setuRole] = useState<string | null>("");
    const [uAccountId, setuAccountId] = useState<string | null>("");

    const fetchAccessToken = useCallback(async () => {
    
        if (accounts.length === 0)
            return;

        let request = {
            scopes: [msalConfig.auth.scopes]
        };
        instance.setActiveAccount(instance.getAccountByUsername(accounts[0].username));

        let authToken = await instance.acquireTokenSilent(request)
        .catch(error => {
            console.warn(error);
            console.warn("silent token acquisition fails. acquiring token using popup");
            //if (error instanceof msal.InteractionRequiredAuthError) {
            if (error != null) {
                // fallback to interaction when silent call fails
                return instance.acquireTokenRedirect(request)
                    .then(response => {
                        console.log(response);
                        return response;
                    }).catch(error => {
                        console.error(error);
                    });
            } else {
                console.warn(error);   
            }
        });
        let accessToken = authToken ? authToken.accessToken : "";

        if (accessToken === "")
            window.location.reload();

        // pull the users name
        Cache.setItem("accessToken", accessToken);

        var user = await UserService.GetCurrentUser();
        if (user.success && user.value) {
            setuName(user.value?.FullName);
            setuRole(user.value?.ApplicationRole);
            setuAccountId(user.value?.AccountId);
        }
        else {
            console.log('An error occurred while loading the current user');
        }
        
        
        setUserLoaded(true);
      }, [accounts, instance]) // if userId changes, useEffect will run again
                   // if you want to run only once, just leave array empty []
    
      useEffect(() => {
        fetchAccessToken()
      }, [fetchAccessToken])

    return (
        <div className="App">

            <AuthenticatedTemplate>
                {!userLoaded ? 
                    <div style={{height:'100vh',width:'100%',display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <CircularProgress/>
                    </div> :
                    <>
                        <div className="main-container">
                            <Router>
                                <div style={{position:'fixed',width:'100%',zIndex:1}}>
                                    <div className="leftRightAppMargins">
                                        <Header/>
                                        <Navbar userName={uName} userRole={uRole} accountId={uAccountId} />
                                        <div className="root-breadcrumb noPrint">
                                            <Breadcrumbs />
                                        </div>
                                    </div>
                                </div>
                                <ScrollToTop />
                                <div className="leftRightAppMargins mainContent">
                                    <Switch>
                                        <Route path='/' exact component={Dashboard} render={props => <Dashboard />} />
                                        <Route path='/AccountProfile' component={AccountProfile} render={props => <AccountProfile/>} />
                                        <Route path='/Announcements' component={Announcements} render={props => <Announcements/>} />
                                        <Route path='/order/:orderNumber' component={Order} render={props => <Order {...props} />} />
                                        <Route path='/invoice/:invoiceNumber' component={Invoice} render={props => <Invoice {...props} />} />
                                    </Switch>
                                </div>
                            </Router>
                            <MediaQuery minWidth={1025}>
                                <div className="leftRightAppMargins">
                                    <Footer />
                                </div>
                            </MediaQuery>
                        </div>
                    </>
                }            
            </AuthenticatedTemplate>

            <UnauthenticatedTemplate>
                <div className="main-container">
                    <Registration />
                </div>
                <MediaQuery minWidth={1025}>
                    <div style={{marginLeft:300,marginRight:300}}>
                        <Footer />
                    </div>
                </MediaQuery>
            </UnauthenticatedTemplate>
        </div>
    );
};

type AppProps = {
    pca: IPublicClientApplication
};

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be 
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication 
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the 
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
export default function App({ pca }: AppProps) {

    const isProduction = process.env.NODE_ENV === 'production';

    return (
        <CacheBuster
        currentVersion={version}
        isEnabled={isProduction} //If false, the library is disabled.
        isVerboseMode={true} //If true, the library writes verbose logs to console.
        loadingComponent={<CircularProgress />} //If not pass, nothing appears at the time of new version check.
      >
            <MsalProvider instance={pca}>
                <StyledEngineProvider injectFirst>
                    <MainContent />
                </StyledEngineProvider>
            </MsalProvider>
        </CacheBuster>
    );
}
